import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IndosbookLogo } from "./IndosbookLogo";
import { useAuth, api } from "../context/AuthContext";
import PostCard from "./PostCard";
import { Button } from "./ui/button";
import { ArrowLeft, Users, Loader2, Plus } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const CommunityPage = () => {
  const { name } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  const [community, setCommunity] = useState(null);
  const [posts, setPosts] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, pRes, aRes] = await Promise.all([
        api.get("/communities"),
        api.get(`/posts/feed?community=${encodeURIComponent(name)}`),
        api.get("/applications"),
      ]);
      setCommunity(cRes.data.communities.find((c) => c.name.toLowerCase() === name.toLowerCase()) || null);
      setPosts(pRes.data.posts);
      setApps(aRes.data.applications);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [name]);

  useEffect(() => { load(); }, [load]);

  const appliedIds = new Set(apps.map((a) => a.job_post_id));

  const toggleJoin = async () => {
    if (!community) return;
    const join = !community.joined;
    try {
      await api.post(`/communities/${community.id}/${join ? "join" : "leave"}`);
      setCommunity((c) => ({ ...c, joined: join, member_count: c.member_count + (join ? 1 : -1) }));
    } catch { toast({ title: "Action failed", variant: "destructive" }); }
  };

  const submitPost = async () => {
    if (!composer.trim()) { toast({ title: "Write something to share." }); return; }
    const title = composer.trim();
    setComposer("");
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("community", community.name);
      const created = (await api.post("/posts", fd)).data;
      setPosts((p) => [{ ...created, time: "just now", upvoted: false }, ...p]);
      toast({ title: "Posted to " + community.name });
    } catch { toast({ title: "Could not post", variant: "destructive" }); }
  };

  const onVote = async (id) => {
    try {
      const res = (await api.post(`/posts/${id}/vote`)).data;
      setPosts((list) => list.map((p) => (p.id === id ? { ...p, upvoted: res.upvoted, upvotes: res.upvotes } : p)));
    } catch { /* ignore */ }
  };

  const applyJob = async (p) => {
    try {
      const created = (await api.post("/applications", {
        job_post_id: p.id, job_title: p.title, company: p.author, rank: p.job?.rank, vessel: p.job?.vessel, salary: p.job?.salary, location: p.job?.location,
      })).data;
      setApps((prev) => [created, ...prev]);
      toast({ title: "Application submitted!" });
    } catch (e) {
      const dup = e?.code === 409 || e?.response?.status === 409;
      toast({ title: dup ? "Already applied" : "Could not apply", variant: dup ? undefined : "destructive" });
    }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-gray-50"><Loader2 className="h-6 w-6 animate-spin text-[#FF4500]" /></div>;
  }

  if (!community) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50 p-6 text-center">
        <p className="text-lg font-bold text-gray-700">Community "{name}" not found.</p>
        <Button onClick={() => navigate("/feed")} className="rounded-full bg-[#FF4500] font-bold">Back to Feed</Button>
      </div>
    );
  }

  const initial = (user?.name || "S").charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white">
        <div className="mx-auto flex h-16 max-w-[800px] items-center gap-4 px-4 sm:px-6">
          <button onClick={() => navigate("/feed")} className="shrink-0"><IndosbookLogo size={28} /></button>
          <Button variant="outline" onClick={() => navigate("/feed")} data-testid="community-back-button" className="ml-auto rounded-full text-sm font-bold">
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Back to Feed
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-[800px] space-y-5 px-4 py-6 sm:px-6">
        {/* Community header */}
        <section className="rounded-2xl border border-gray-200 bg-white p-6" data-testid="community-header">
          <div className="flex flex-wrap items-center gap-4">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-slate-100 text-2xl font-black text-slate-700">
              {community.name.charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-extrabold text-gray-900" data-testid="community-name">{community.name}</h1>
              <p className="text-sm text-gray-500">{community.description || "A merchant navy community."}</p>
              <p className="mt-1 flex items-center gap-1 text-xs font-bold text-slate-500"><Users className="h-3.5 w-3.5" /> {community.member_count.toLocaleString()} members</p>
            </div>
            <Button onClick={toggleJoin} data-testid="community-join-button" variant={community.joined ? "outline" : "default"}
              className={`rounded-full px-5 font-bold ${community.joined ? "border-2 border-gray-200 text-gray-600" : "bg-[#002855] text-white hover:bg-[#001d40]"}`}>
              {community.joined ? "Joined" : "Follow"}
            </Button>
          </div>
        </section>

        {/* Composer (members only) */}
        {community.joined && (
          <section className="rounded-2xl border border-gray-200 bg-white p-4" data-testid="community-composer">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#FF4500] text-sm font-black text-white">{initial}</span>
              <input value={composer} onChange={(e) => setComposer(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitPost()}
                placeholder={`Post in ${community.name}…`} data-testid="community-composer-input"
                className="h-11 flex-1 rounded-full bg-gray-100 px-5 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-300" />
              <Button onClick={submitPost} data-testid="community-post-button" className="h-10 shrink-0 rounded-full bg-[#FF4500] px-5 text-sm font-bold text-white hover:bg-[#e03d00]"><Plus className="mr-1 h-4 w-4" /> Post</Button>
            </div>
          </section>
        )}

        {/* Posts */}
        <div className="space-y-4">
          {posts.length === 0 && (
            <p className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-400" data-testid="community-empty">
              No posts in {community.name} yet.{community.joined ? " Be the first to post!" : " Follow to join the conversation."}
            </p>
          )}
          {posts.map((p) => (
            <PostCard key={p.id} post={p} score={p.upvotes} upvoted={p.upvoted} onVote={onVote} onApply={applyJob} applied={appliedIds.has(p.id)} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommunityPage;
